<template>
  <div class="material-search">
    <div class="search-header">
      <div class="search-tabs">
        <span 
          :class="{ active: searchMode === 'video' }" 
          @click="searchMode = 'video'"
        >视频搜索</span>
        <span 
          :class="{ active: searchMode === 'commodity' }" 
          @click="searchMode = 'commodity'"
        >商品讲解片段</span>
        <span 
          :class="{ active: searchMode === 'commodity_group' }" 
          @click="searchMode = 'commodity_group'"
        >商品讲解(分组)</span>
        <span 
          :class="{ active: searchMode === 'hot_video' }" 
          @click="searchMode = 'hot_video'"
        >热点视频</span>
      </div>
    </div>

    <div class="search-bar">
      <!-- Influencer Filter -->
      <div class="filter-row" style="display: flex; gap: 10px; align-items: flex-start;">
        <v-autocomplete
          style="flex: 1;"
          v-model="selectedKols"
          :items="kolList"
          item-title="name"
          item-value="id"
          label="筛选达人"
          multiple
          chips
          closable-chips
          variant="outlined"
          density="compact"
          hide-details
          bg-color="white"
          @update:modelValue="search"
        ></v-autocomplete>
        <v-checkbox
          v-model="showAdvanced"
          label="按日期筛选"
          hide-details
          density="compact"
          color="primary"
          style="flex: 0 0 auto; margin-top: 2px;"
        ></v-checkbox>
      </div>

      <!-- Advanced Filters -->
      <div v-show="showAdvanced" class="advanced-filters" style="display: flex; gap: 10px;">
        <v-text-field
          v-model="startDate"
          label="开始日期"
          type="date"
          variant="outlined"
          density="compact"
          hide-details
          bg-color="white"
        ></v-text-field>
        <v-text-field
          v-model="endDate"
          label="结束日期"
          type="date"
          variant="outlined"
          density="compact"
          hide-details
          bg-color="white"
        ></v-text-field>
      </div>

      <div class="input-row">
        <input 
          type="text" 
          v-model="keyword" 
          @keyup.enter="search"
          :placeholder="getPlaceholder()" 
        />
        <button @click="search" :disabled="loading">
          {{ loading ? '搜索中...' : '搜索' }}
        </button>
      </div>
    </div>
    <div class="search-results">
      <template v-for="(item, index) in results" :key="searchMode + '_' + index">
      <div 
        v-if="searchMode !== 'commodity_group'"
        class="result-item"
        @click="selectMaterial(item)"
        :class="{ active: selectedItem === item, disabled: isItemDisabled(item), 'active-playing': activeSegment === item }"
      >
        <template v-if="searchMode === 'video'">
          <div class="item-title">{{ item.video_name || item.title }}</div>
          <div class="item-details" style="margin-bottom: 4px; display: flex; flex-direction: column; gap: 4px; align-items: flex-start;">
             <span class="tag-influencer" v-if="item['达人昵称']" :title="item['达人昵称']"><i class="mdi mdi-account-circle-outline"></i> {{ item['达人昵称'] }}</span>
             <span class="tag-abstract" v-if="item.abstract">{{ item.abstract }}</span>
             
             <!-- Search Keyword Highlights -->
             <div v-if="item.matches && item.matches.length > 0" class="matches-container">
               <div v-for="(match, mIdx) in item.matches" :key="mIdx" class="match-item" v-html="match.highlight_text"></div>
             </div>
          </div>
          <div class="item-info">
             <span v-if="item.video_start_time" title="开播时间"><i class="mdi mdi-clock-outline"></i> {{ formatDateTime(item.video_start_time) }}</span>
             <span v-if="item.end_ts" title="时长"><i class="mdi mdi-timer-outline"></i> {{ item.end_ts }}</span>
             <span v-if="item.duration_str">{{ item.duration_str }}</span>
          </div>
        </template>
        <template v-else-if="searchMode === 'hot_video'">
          <div class="item-title" v-html="item.highlight_title || item.title"></div>
          <div class="item-details" style="margin-bottom: 4px; display: flex; flex-direction: column; gap: 4px; align-items: flex-start;">
             <div style="display: flex; gap: 8px; flex-wrap: wrap;">
               <span class="tag-influencer" v-if="item.author" :title="item.author"><i class="mdi mdi-account-circle-outline"></i> {{ item.author }}</span>
               <span class="tag-product" v-if="item.product_name" :title="item.product_name"><i class="mdi mdi-shopping-outline"></i> {{ item.product_name }}</span>
             </div>
             
             <!-- Search Keyword Highlights -->
             <div v-if="item.matches && item.matches.length > 0" class="matches-container">
               <div v-for="(match, mIdx) in item.matches" :key="mIdx" class="match-item" v-html="match.highlight_text"></div>
             </div>
          </div>
          <div class="item-info">
             <span v-if="item.heat_value" class="tag-info">热度: {{ item.heat_value }}</span>
             <span v-if="item.sales_volume && !['0', 0, '0.0'].includes(item.sales_volume)" class="sales-info">销量: {{ formatNumber(item.sales_volume) }}</span>
             <span v-if="item.sales_amount" class="sales-info">销售额: {{ formatNumber(item.sales_amount) }}</span>
             <span v-if="item.days_on_list > 0" class="tag-info">上榜{{ item.days_on_list }}天</span>
             <span class="publish-time">{{ item.publish_time || '' }}</span>
          </div>
        </template>
        <template v-else>
          <div style="display: flex; gap: 10px;">
            <div v-if="item['商品图片'] || item['商品图片_本地']" class="item-image" style="flex-shrink: 0;">
              <img :src="item['商品图片_本地'] || item['商品图片']" alt="商品图片" style="width: 80px; height: 80px; object-fit: cover; border-radius: 4px;" />
            </div>
            <div style="flex: 1; min-width: 0;">
              <div class="item-title" v-html="item['商品名称']"></div>
              <div class="item-details" style="margin-bottom: 4px; display: flex; flex-direction: column; gap: 4px; align-items: flex-start;" v-if="item['达人昵称'] || item.talent_nickname">
                 <span class="tag-influencer" :title="item['达人昵称'] || item.talent_nickname"><i class="mdi mdi-account-circle-outline"></i> {{ item['达人昵称'] || item.talent_nickname }}</span>
              </div>
              <div class="item-info">
                <span v-if="item['已录制']" class="tag-recorded">已录制</span>
                <span v-else class="tag-unrecorded">未录制</span>
                {{ formatTime(item['讲解开始时间']) }}
              </div>
              <div class="item-details">
                <span v-if="item['讲解时长（分钟）']">时长: {{ Number(item['讲解时长（分钟）']).toFixed(1) }}分</span>
                <span v-if="item['全网销量'] && !['0', 0, '0.0'].includes(item['全网销量'])" class="sales-info">销量: {{ item['全网销量'] }}</span>
              </div>
            </div>
          </div>
        </template>
      </div>

      <CommodityGroupItem
        v-else-if="searchMode === 'commodity_group'"
        :group="item"
        :expanded="expandedGroups.includes(index)"
        :selected-segment="selectedItem"
        :active-segment="activeSegment"
        @toggle="toggleGroup(index)"
        @select="selectMaterial"
      />
    </template>
      
      <p v-if="!loading && results.length === 0" style="color: #999; text-align: center; margin-top: 20px;">
        暂无结果
      </p>
    </div>

    <Teleport to="body">
      <div v-if="showHelp" class="help-overlay" @click.self="showHelp = false">
        <div class="help-modal">
          <div class="help-header">
            <h3>素材搜索功能指南</h3>
            <button class="close-btn" @click="showHelp = false">×</button>
          </div>
          <div class="help-content">
            <div class="help-step">
              <div class="step-icon"><i class="mdi mdi-video"></i></div>
              <div class="step-text">
                <div class="step-title">1. 选择搜索模式</div>
                <p>支持 <b>常规视频</b>（带字幕/音频）、<b>商品维度</b>（看讲解分布）和 <b>实时热点</b>（爆款趋势）。</p>
              </div>
            </div>
            <div class="help-step">
              <div class="step-icon"><i class="mdi mdi-filter-variant"></i></div>
              <div class="step-text">
                <div class="step-title">2. 达人与日期筛选</div>
                <p>可通过搜索选择特定达人，或按开播时间范围进行精确查询。</p>
              </div>
            </div>
            <div class="help-step">
              <div class="step-icon"><i class="mdi mdi-layers-outline"></i></div>
              <div class="step-text">
                <div class="step-title">3. 商品分组功能</div>
                <p>常规商品搜索默认按流平铺；"按商品分组"模式会自动聚合同一商品在不同时段的讲解，方便对比。</p>
              </div>
            </div>
            <div class="help-step">
              <div class="step-icon"><i class="mdi mdi-playlist-play"></i></div>
              <div class="step-text">
                <div class="step-title">4. 点击快速预览</div>
                <p>点击搜索结果，主播放器将自动定位到对应时间点。已录制素材会有深色边框标识。</p>
              </div>
            </div>
          </div>
          <div class="help-footer">
            <v-btn color="primary" block @click="showHelp = false">我知道了</v-btn>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useMaterialSearchPersistence } from '@/composables/useMaterialSearchPersistence'
import CommodityGroupItem from '@/components/common/CommodityGroupItem.vue'
import { normalizeCommodityGroups } from '@/utils/commodityGroups'

import { 
  get_accessable_kol_list, 
  get_clip_material_list, 
  search_hot_videos, 
  query_outter_commodity_explanation, 
  query_outter_commodity_group,
  get_hot_video_source_trace 
} from '@/services/api.js'

export default {
  name: 'MaterialSearch',
  components: { CommodityGroupItem },
  props: {
    currentTime: {
      type: Number,
      default: 0
    }
  },
  setup(props) {
    const store = useStore()
    const {
      keyword: videoKeyword,
      selectedKols: videoSelectedKols,
      showAdvanced: videoShowAdvanced,
      startDate: videoStartDate,
      endDate: videoEndDate
    } = useMaterialSearchPersistence()
    const previewSource = computed(() => store.state.multiClipper.previewSource)
    const showHelp = ref(false)
    const keyword = ref(videoKeyword.value)
    
    // 按模式保存搜索关键词
    const modeKeywords = ref({
      video: videoKeyword.value,
      commodity: '',
      commodity_group: '',
      hot_video: ''
    })

    // 关键词映射逻辑：商品讲解片段和分组共享同一个关键词，其他独立
    const getKeywordKey = (mode) => {
      if (mode === 'commodity' || mode === 'commodity_group') return 'commodity'
      return mode
    }

    // 根据模式获取达人筛选的存储键
    const getKolStorageKey = (mode) => {
      if (mode === 'hot_video') return 'material_search_kols_hot'
      if (mode === 'video') return 'material_search_kols_normal'
      return 'material_search_kols_commodity'
    }

    // 从 sessionStorage 恢复关键词
    const savedKeywords = sessionStorage.getItem('material_search_keywords')
    if (savedKeywords) {
      try {
        const parsed = JSON.parse(savedKeywords)
        Object.assign(modeKeywords.value, parsed)
      } catch (e) {
        console.error('Failed to parse saved keywords', e)
      }
    }

    // 按模式保存搜索结果和状态
    const modeResults = ref({
      video: [],
      commodity: [],
      commodity_group: [],
      hot_video: []
    })
    const modeSelectedItems = ref({
      video: null,
      commodity: null,
      commodity_group: null,
      hot_video: null
    })
    const modeActiveSegments = ref({
      video: null,
      commodity: null,
      commodity_group: null,
      hot_video: null
    })
    const modeExpandedGroups = ref({
      commodity_group: []
    })

    /** @type {import('vue').Ref<any[]>} */
    const results = ref([])
    const loading = ref(false)
    const searchMode = ref('video') // 'video' | 'commodity' | 'hot_video'
    let previousMode = 'video'
    
    // 初始化当前关键词
    keyword.value = modeKeywords.value[getKeywordKey(searchMode.value)] || ''

    const selectedItem = ref(null)
    const activeSegment = ref(null)
    let lastManualClickTime = 0

    // Advanced search
    const showAdvanced = ref(videoShowAdvanced.value)
    const startDate = ref(videoStartDate.value)
    const endDate = ref(videoEndDate.value)
    
    // Influencer filter
    /** @type {import('vue').Ref<any[]>} */
    const kolList = ref([])
    /** @type {import('vue').Ref<any[]>} */
    const selectedKols = ref([...videoSelectedKols.value])

    // Clear results when switching modes
    watch(searchMode, (newMode) => {
      // 1. 保存当前模式的状态
      modeResults.value[previousMode] = [...results.value]
      modeSelectedItems.value[previousMode] = selectedItem.value
      modeActiveSegments.value[previousMode] = activeSegment.value
      if (previousMode === 'commodity_group') {
        modeExpandedGroups.value.commodity_group = [...expandedGroups.value]
      }

      // 2. 恢复新模式的状态
      results.value = modeResults.value[newMode] || []
      selectedItem.value = modeSelectedItems.value[newMode] || null
      activeSegment.value = modeActiveSegments.value[newMode] || null
      if (newMode === 'commodity_group') {
        expandedGroups.value = modeExpandedGroups.value.commodity_group || []
      }
      
      // 恢复对应模式的关键词
      keyword.value = modeKeywords.value[getKeywordKey(newMode)] || ''
      
      // 根据模式回显不同的达人选择记录
      if (newMode === 'video') {
        selectedKols.value = [...videoSelectedKols.value]
        showAdvanced.value = videoShowAdvanced.value
        startDate.value = videoStartDate.value
        endDate.value = videoEndDate.value
      } else {
        const storageKey = getKolStorageKey(newMode)
        const savedKols = sessionStorage.getItem(storageKey)
        if (savedKols) {
          try {
            selectedKols.value = JSON.parse(savedKols)
          } catch (e) {
            selectedKols.value = []
          }
        } else {
          selectedKols.value = []
        }
      }

      fetchKolList() // Re-fetch KOL list based on mode
      
      // 如果新模式没有结果，则自动触发一次搜索（可选，但通常用户期望看到内容）
      // 如果已经有结果了，就不再自动触发搜索，保留原来的状态
      if (results.value.length === 0) {
        search()
      }

      previousMode = newMode
    })

    // 会话级别保存关键词
    watch(keyword, (newVal) => {
      const bucket = getKeywordKey(searchMode.value)
      modeKeywords.value[bucket] = newVal
      if (searchMode.value === 'video') {
        videoKeyword.value = newVal
      } else {
        sessionStorage.setItem('material_search_keywords', JSON.stringify(modeKeywords.value))
      }
    })

    // 会话级别保存达人选择
    watch(selectedKols, (newVal) => {
      if (searchMode.value === 'video') {
        if (JSON.stringify(newVal) !== JSON.stringify(videoSelectedKols.value)) {
          videoSelectedKols.value = [...newVal]
        }
      } else {
        const storageKey = getKolStorageKey(searchMode.value)
        sessionStorage.setItem(storageKey, JSON.stringify(newVal))
      }
    }, { deep: true })

    watch(videoKeyword, (newVal) => {
      modeKeywords.value.video = newVal
      if (searchMode.value === 'video' && keyword.value !== newVal) keyword.value = newVal
    })

    watch(videoSelectedKols, (newVal) => {
      if (searchMode.value === 'video' && JSON.stringify(selectedKols.value) !== JSON.stringify(newVal)) {
        selectedKols.value = [...newVal]
      }
    }, { deep: true })

    watch(showAdvanced, (newVal) => {
      if (searchMode.value === 'video') videoShowAdvanced.value = newVal
    })

    watch(startDate, (newVal) => {
      if (searchMode.value === 'video') videoStartDate.value = newVal
    })

    watch(endDate, (newVal) => {
      if (searchMode.value === 'video') videoEndDate.value = newVal
    })

    watch(videoShowAdvanced, (newVal) => {
      if (searchMode.value === 'video' && showAdvanced.value !== newVal) showAdvanced.value = newVal
    })

    watch(videoStartDate, (newVal) => {
      if (searchMode.value === 'video' && startDate.value !== newVal) startDate.value = newVal
    })

    watch(videoEndDate, (newVal) => {
      if (searchMode.value === 'video' && endDate.value !== newVal) endDate.value = newVal
    })

    const getPlaceholder = () => {
      if (searchMode.value === 'video') return '搜索视频/字幕...'
      if (searchMode.value === 'hot_video') return '搜索热点视频标题...'
      return '搜索商品名称...'
    }

    const expandedGroups = ref([])

    const toggleGroup = (index) => {
      if (expandedGroups.value.includes(index)) {
        expandedGroups.value = expandedGroups.value.filter(i => i !== index)
      } else {
        expandedGroups.value.push(index)
      }
    }

    /**
     * 判断某个素材项是否与当前的播放时间匹配
     */
    const checkItemMatch = (item, currentAbsoluteTimeMs) => {
      if (!item || !item['讲解开始时间']) return false
      const startMs = new Date(item['讲解开始时间']).getTime()
      const durationMin = parseFloat(item['讲解时长（分钟）'] || 0)
      const endMs = startMs + durationMin * 60 * 1000
      return currentAbsoluteTimeMs >= startMs && currentAbsoluteTimeMs < endMs
    }

    /**
     * 定位逻辑：根据当前播放时间或内容在搜索结果中寻找匹配项
     * @param {boolean} shouldScroll 是否执行滚动定位和自动展开
     */
    const locateCurrentPlaying = (shouldScroll = false) => {
      const mode = searchMode.value
      const source = previewSource.value
      const time = props.currentTime
      
      // 没有任何预览源或结果时直接返回
      if (!source || results.value.length === 0) return

      let found = null

      if (mode === 'video') {
        // 视频搜索模式：匹配 transcription_id
        found = results.value.find(item => item.transcription_id === source.transcription_id)
      } else if (mode === 'hot_video') {
        // 热点视频模式：匹配 video_id
        found = results.value.find(item => item.video_id === source.video_id)
      } else if (mode === 'commodity' || mode === 'commodity_group') {
        // 商品讲解模式：保留原有的时间片匹配逻辑
        const isGroup = mode === 'commodity_group'
        const videoStartTime = source.video_start_time || source.shooting_time
        if (!videoStartTime) return

        const videoStartMs = new Date(videoStartTime).getTime()
        const currentAbsoluteTimeMs = videoStartMs + time * 1000

        // 优先级 1: 当前选中项是否仍匹配
        if (selectedItem.value && checkItemMatch(selectedItem.value, currentAbsoluteTimeMs)) {
          found = selectedItem.value
        }

        if (!found) {
          const currentInfluencer = source['达人昵称'] || source.talent_nickname || source.author
          const currentCommodity = source['商品名称'] || source.current_commodity || source.product_name

          // 优先级 2 & 3: 在结果中查找匹配项
          if (isGroup) {
            // 分组模式逻辑
            if (currentInfluencer && currentCommodity) {
              const group = results.value.find(g => 
                (g.talentNickname === currentInfluencer) && (g.productName === currentCommodity)
              )
              if (group && group.segments) {
                found = group.segments.find(s => checkItemMatch(s, currentAbsoluteTimeMs))
              }
            }
            if (!found && currentInfluencer) {
              const influencerGroups = results.value.filter(g => g.talentNickname === currentInfluencer)
              for (const group of influencerGroups) {
                found = group.segments?.find(s => checkItemMatch(s, currentAbsoluteTimeMs))
                if (found) break
              }
            }
          } else {
            // 平铺列表模式逻辑
            if (currentInfluencer && currentCommodity) {
              found = results.value.find(s => 
                (s['达人昵称'] || s.talent_nickname === currentInfluencer) && 
                (s['商品名称'] || s.current_commodity === currentCommodity) && 
                checkItemMatch(s, currentAbsoluteTimeMs)
              )
            }
            if (!found && currentInfluencer) {
              found = results.value.find(s => 
                (s['达人昵称'] || s.talent_nickname === currentInfluencer) && 
                checkItemMatch(s, currentAbsoluteTimeMs)
              )
            }
          }
          
          // 兜底：查找任意匹配的片段
          if (!found) {
            if (isGroup) {
              for (const group of results.value) {
                  found = group.segments?.find(s => checkItemMatch(s, currentAbsoluteTimeMs))
                  if (found) break
              }
            } else {
              found = results.value.find(s => checkItemMatch(s, currentAbsoluteTimeMs))
            }
          }
        }
      }

      // 处理找到的结果
      if (found) {
        if (found !== activeSegment.value) {
          // 跳转保护：如果是自动随动（shouldScroll=false）且在点击后的短时间内，
          // 且找到的不是刚才点的那个，说明是播控状态还没同步，忽略此次更新
          if (!shouldScroll && Date.now() - lastManualClickTime < 2000 && found !== selectedItem.value) {
            return
          }
          activeSegment.value = found
        }
        
        // 只有主动定位（按钮触发）或者满足特定条件的随动才执行滚动和展开
        if (shouldScroll) {
          lastManualClickTime = Date.now() // 主动点击定位时，也开启短时间保护
          
          if (mode === 'commodity_group') {
            // 自动展开分组
            const groupIndex = results.value.findIndex(g => g.segments?.some(s => s === found))
            if (groupIndex !== -1 && !expandedGroups.value.includes(groupIndex)) {
              expandedGroups.value.push(groupIndex)
            }
          }

          // 滚动定位
          nextTick(() => {
            const selector = mode === 'commodity_group' ? '.segment-item.active-playing' : '.result-item.active-playing'
            const el = document.querySelector(selector)
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
            }
          })
        }
      } else {
        activeSegment.value = null
      }
    }

    // 定位逻辑：观察播放时间变化，仅更新高亮状态，不自动滚动
    watch([() => props.currentTime, () => previewSource.value], () => {
      locateCurrentPlaying(false)
    })

    const groupCommodityData = (data) => {
      if (!data || data.length === 0) return [];
      
      const groups = new Map();
      
      data.forEach(item => {
        const key = `${item['达人昵称'] || item.talent_nickname || '未知达人'}_${item['商品名称'] || item.current_commodity || '未知商品'}`;
        
        if (!groups.has(key)) {
          groups.set(key, {
            talentNickname: item['达人昵称'] || item.talent_nickname || '未知达人',
            productName: item['商品名称'] || item.current_commodity || '未知商品',
            productImage: item['商品图片_本地'] || item['商品图片'],
            segments: [],
            expanded: false
          });
        }
        
        groups.get(key).segments.push(item);
      });
      
      return Array.from(groups.values()).map(group => {
        // 先按时间正序排，以便计算每日序号 (第1段, 第2段...)
        group.segments.sort((a, b) => {
          const timeA = new Date(a['讲解开始时间'] || 0).getTime();
          const timeB = new Date(b['讲解开始时间'] || 0).getTime();
          return timeA - timeB;
        });

        // 计算每日序号
        const dateCounters = {};
        group.segments.forEach(seg => {
          const dateStr = (seg['讲解开始时间'] || '').split(' ')[0];
          if (dateStr) {
            if (!dateCounters[dateStr]) dateCounters[dateStr] = 0;
            dateCounters[dateStr]++;
            seg._dailyIndex = dateCounters[dateStr];
          } else {
            seg._dailyIndex = 0;
          }
        });

        // 最终展示排序：日期倒序（最新的日期在最上），但日期内部按时间正序（顺序排）
        group.segments.sort((a, b) => {
          const dateA = (a['讲解开始时间'] || '').split(' ')[0];
          const dateB = (b['讲解开始时间'] || '').split(' ')[0];
          if (dateA !== dateB) {
            return new Date(dateB || 0).getTime() - new Date(dateA || 0).getTime();
          }
          const timeA = new Date(a['讲解开始时间'] || 0).getTime();
          const timeB = new Date(b['讲解开始时间'] || 0).getTime();
          return timeA - timeB;
        });

        const recordedSegments = group.segments.filter(s => s['已录制']).length;
        const notRecordedSegments = group.segments.length - recordedSegments;
        const totalDuration = group.segments.reduce((sum, segment) => {
          const duration = parseFloat(segment['讲解时长（分钟）'] || 0) || 0;
          return sum + duration;
        }, 0);
        
        return {
          ...group,
          recordedSegments,
          notRecordedSegments,
          totalDuration: totalDuration > 0 ? `${totalDuration.toFixed(1)}分钟` : '-'
        };
      }).sort((a, b) => {
        // 总列表按各组内最晚的时间倒序排（最新活跃的商品排在前面）
        const latestA = Math.max(...a.segments.map(s => new Date(s['讲解开始时间'] || 0).getTime()));
        const latestB = Math.max(...b.segments.map(s => new Date(s['讲解开始时间'] || 0).getTime()));
        return latestB - latestA;
      });
    }

    const isItemDisabled = (item) => {
      if (searchMode.value === 'commodity' || searchMode.value === 'commodity_group') {
        return !item['已录制']
      }
      return false
    }

    onMounted(async () => {
      // Initial search
      search()
      // Initial fetch based on default mode
      fetchKolList()
    })

    const fetchKolList = async () => {
      // Fetch available influencers
      try {
        let type = undefined
        if (searchMode.value === 'hot_video') {
            type = 'virtual'
        } else if (searchMode.value === 'video') {
            type = 'douyin' // 视频搜索固定只显示实体达人
        }
        
        const res = await get_accessable_kol_list(type)
        // Robustly handle different response structures
        // SelectVideo.vue suggests the response might be the array directly
        let data = res
        if (res.data) {
           data = res.data
           if (data.data) {
               data = data.data
           }
        }
        
        if (Array.isArray(data)) {
           kolList.value = data
        } else {
           console.warn('Unexpected kol list format:', res)
           kolList.value = []
        }
      } catch (e) {
        console.error('Failed to fetch kol list', e)
      }
    }

    const search = async () => {
      loading.value = true
      results.value = []
      activeSegment.value = null
      try {
        const kolListParam = selectedKols.value.length > 0 ? selectedKols.value.join(',') : undefined

        if (searchMode.value === 'video') {
          const res = await get_clip_material_list({ 
            key_word: keyword.value,
            kol_list: kolListParam,
            start_date: (showAdvanced.value && startDate.value) ? startDate.value : undefined,
            end_date: (showAdvanced.value && endDate.value) ? endDate.value : undefined,
            exclude_virtual: true // 视频搜索模式固定排除虚拟达人
          })
          if (res.data) {
             results.value = res.data
          } else {
             results.value = res
          }
        } else if (searchMode.value === 'hot_video') {
          // Use the first selected KOL as uid filter if available
          const uid = selectedKols.value.length > 0 ? selectedKols.value[0] : undefined
          const res = await search_hot_videos(
            keyword.value, 
            uid, 
            (showAdvanced.value && startDate.value) ? startDate.value : undefined, 
            (showAdvanced.value && endDate.value) ? endDate.value : undefined
          )
          if (res && res.data) {
            results.value = res.data
          } else {
            results.value = res || []
          }
        } else {
          // Commodity Search
          let res;
          if (searchMode.value === 'commodity_group') {
            res = await query_outter_commodity_group({
              key_word: keyword.value,
              kol_list: kolListParam,
              start_date: (showAdvanced.value && startDate.value) ? startDate.value : undefined,
              end_date: (showAdvanced.value && endDate.value) ? endDate.value : undefined
            })
          } else {
            res = await query_outter_commodity_explanation({ 
              key_word: keyword.value,
              kol_list: kolListParam,
              start_date: (showAdvanced.value && startDate.value) ? startDate.value : undefined,
              end_date: (showAdvanced.value && endDate.value) ? endDate.value : undefined
            })
          }
          
          let data = []
          if (res && res.data && res.data.data) {
            data = res.data.data
          } else if (res && res.data) {
            data = res.data
          } else {
            data = res
          }
          if (typeof data === 'string') {
              try {
                  data = JSON.parse(data)
              } catch(e) {
                  data = []
              }
          }
          
          if (searchMode.value === 'commodity_group') {
              results.value = normalizeCommodityGroups(data)
              expandedGroups.value = [] // reset expanded groups
          } else {
              results.value = data
          }
        }
      } catch (e) {
        console.error(e)
        results.value = []
      } finally {
        loading.value = false
      }
    }

    const selectMaterial = async (item) => {
      // Set selected item first for immediate feedback
      selectedItem.value = item
      activeSegment.value = item
      lastManualClickTime = Date.now()

      // 检查关键词是否包含时间特征 (如 : / - 或纯数字秒数)
      const isTimeKeyword = keyword.value && (
        keyword.value.includes(':') || 
        keyword.value.includes('/') || 
        keyword.value.includes('-') || 
        /^\d+(\.\d+)?$/.test(keyword.value.trim())
      )

      // 优先使用后端返回的命中时间 hit_time (格式已标准化为 YYYY-MM-DD HH:MM:SS)，
      // 否则回退到用关键词自行解析
      const searchTime = item.hit_time || (isTimeKeyword ? keyword.value : undefined)

      if (searchMode.value === 'commodity' || searchMode.value === 'commodity_group') {
        if (!item['已录制']) {
          alert('该讲解片段尚未录制，无法查看。')
          return
        }
        // Transform to preview source format
        // Keep HTML tags for the title so SourceViewer can render them
        const source = {
          ...item,
          transcription_id: item['录制ID'],
          video_name: item['商品名称'] + ' - 讲解片段',
          // Pass matches to trigger highlighting in SourceViewer
          matches: item.matches || [],
          search_time: searchTime
        }
        store.dispatch('multiClipper/selectPreview', source)
      } else if (searchMode.value === 'hot_video') {
        // Hot Video Logic
        // Check video_id instead of transcription_id for fetch, but keep transcription_id check for safety if needed
        if (!item.video_id) {
           console.warn('[MaterialSearch] Missing video_id for item:', item)
           alert('该热点视频尚未完成处理或无有效视频文件，暂无溯源信息可供查看。')
           return
        }
        
        // console.log('[MaterialSearch] Fetching source trace for video_id:', item.video_id)

        // 1. Fetch Source Trace Details
        try {
            loading.value = true
            // Pass video_id
            const res = await get_hot_video_source_trace(item.video_id)
            
            // console.log('[MaterialSearch] Source trace response:', res)

            let sourceTrace = res
            // if (res.data && Array.isArray(res.data)) {
            //     sourceTrace = res.data
            // } else if (res.data && res.data.data) {
            //      // Handle nested data if API wrapper or backend structure varies
            //      sourceTrace = res.data.data
            // }
            
            // console.log('[MaterialSearch] Parsed source trace:', sourceTrace)

            // 2. Construct Preview Source
             const source = {
               ...item,
               video_name: item.title,
               transcription_id: item.transcription_id,
               is_hot_video: true,
               search_time: searchTime
               // Do NOT pass source_trace to selectPreview to avoid ClipPool auto-adding duplicate group
               // source_trace: sourceTrace 
             }
             
             // console.log('[MaterialSearch] Dispatching preview source:', source)
             store.dispatch('multiClipper/selectPreview', source)

             // 3. Add segments to Clip Pool
             if (sourceTrace && sourceTrace.length > 0) {
                // console.log('[MaterialSearch] Adding segments to Clip Pool...')
                const segments = sourceTrace
                    //.filter(trace => trace.video_url)
                    .map((trace, idx) => ({
                        ...trace,
                        id: Date.now().toString() + '_' + idx,
                        text: trace.text,
                        author: trace.author || item.author,
                        // Convert seconds to milliseconds for ClipPool
                        start_time: trace.start_time ? trace.start_time * 1000 : 0,
                        end_time: trace.end_time ? trace.end_time * 1000 : 0,
                        video_url: trace.video_url,
                        video_id: trace.video_id, // Ensure video_id is passed for stable playback
                        transcription_id: trace.source_from,
                        // Use text preview as name or generic name
                        video_name: trace.source_title || trace.title || (trace.text ? (trace.text.length > 10 ? trace.text.substring(0, 10) + '...' : trace.text) : `片段 ${idx+1}`),
                        hot_video_url: trace.hot_video_url,
                        hot_start_time: trace.hot_start_time ? trace.hot_start_time * 1000 : 0,
                        hot_end_time: trace.hot_end_time ? trace.hot_end_time * 1000 : 0,
                        // Map source_spans to words for Fine Tuning
                        words: trace.source_spans ? trace.source_spans.map(w => ({
                             text: w.text || w.word,
                             // source_spans usually have ms timestamps (int), trace has s (float)
                             // Use as is, but ensure valid keys
                             start_time: w.start_time,
                             end_time: w.end_time,
                             // Preserve other fields if needed
                             ...w
                        })) : []
                    }));
                
                if (segments.length > 0) {
                    store.dispatch('multiClipper/setSourceTraceSegments', segments);
                    // console.log('[MaterialSearch] Set source trace segments:', segments.length)
                } else {
                    console.warn('[MaterialSearch] No valid segments found in trace.')
                }
             } else {
                 // console.log('[MaterialSearch] No source trace items to add to pool.')
             }
             
         } catch (e) {
            console.error("Failed to fetch hot video details", e)
            alert("获取热点视频详情失败")
        } finally {
            loading.value = false
        }

      } else {
        const source = {
          ...item,
          search_time: searchTime
        }
        store.dispatch('multiClipper/selectPreview', source)
      }
      
      store.commit('multiClipper/SET_ACTIVE_PANEL', 'source')
    }

    const formatDateHeader = (timeStr) => {
      if (!timeStr) return ''
      try {
        const dateStr = timeStr.split(' ')[0]
        const dateParts = dateStr.split('-')
        if (dateParts.length === 3) {
          return `${parseInt(dateParts[1])}月${parseInt(dateParts[2])}日`
        }
        return dateStr
      } catch (e) {
        return timeStr
      }
    }

    const formatTime = (timeStr) => {
      if (!timeStr) return ''
      // Return full time string to include date as requested
      // Assuming timeStr is "YYYY-MM-DD HH:mm:ss"
      // Can simplify if seconds are not needed: timeStr.substring(0, 16)
      return timeStr
    }

    const formatDateTime = (timeStr) => {
      if (!timeStr) return ''
      try {
        const date = new Date(timeStr)
        if (isNaN(date.getTime())) return timeStr
        
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        
        return `${year}-${month}-${day} ${hours}:${minutes}`
      } catch (e) {
        return timeStr
      }
    }

    const formatNumber = (num, unit = '') => {
      if (num === undefined || num === null || num === '' || num === '-') return ''
      const n = Number(num)
      if (isNaN(n)) return num
      
      if (n >= 100000000) {
        return parseFloat((n / 100000000).toFixed(2)) + '亿' + unit
      } else if (n >= 10000) {
        return parseFloat((n / 10000).toFixed(2)) + 'W' + unit
      } else if (n >= 1000) {
        return parseFloat((n / 1000).toFixed(2)) + 'K' + unit
      } else {
        return parseFloat(n.toFixed(2)) + unit
      }
    }

    return {
      keyword,
      results,
      loading,
      searchMode,
      search,
      selectMaterial,
      selectedItem,
      formatTime,
      formatDateHeader,
      formatDateTime,
      formatNumber,
      kolList,
      selectedKols,
      showAdvanced,
      startDate,
      endDate,
      getPlaceholder,
      isItemDisabled,
      showHelp,
      expandedGroups,
      toggleGroup,
      activeSegment,
      locateCurrentPlaying
    }
  }
}
</script>

<style scoped>
.help-btn-small {
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  transition: color 0.2s;
}

.help-btn-small:hover {
  color: #1976d2;
}

/* Help Modal Styles (Scoped) */
.help-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
}

.help-modal {
  background: white;
  width: 500px;
  max-width: 90vw;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
}

.help-header {
  padding: 15px 20px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.help-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  line-height: 1;
}

.close-btn:hover {
  color: #666;
}

.help-content {
  padding: 20px;
  max-height: 70vh;
  overflow-y: auto;
}

.help-step {
  display: flex;
  margin-bottom: 20px;
  gap: 15px;
}

.step-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #e3f2fd;
  color: #1976d2;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 18px;
  flex-shrink: 0;
}

.step-info h4 {
  margin: 0 0 5px 0;
  color: #333;
  font-size: 15px;
}

.step-info p {
  margin: 0;
  color: #666;
  font-size: 13px;
  line-height: 1.5;
}

.help-footer {
  padding: 15px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
}

.primary-btn {
  background-color: #1976d2;
  color: white;
  border: none;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.primary-btn:hover {
  background-color: #1565c0;
}

.tag-warning {
  display: inline-block;
  padding: 2px 6px;
  background-color: #fff3e0;
  color: #f57c00;
  border-radius: 4px;
  font-size: 11px;
  margin-right: 5px;
  border: 1px solid #ffe0b2;
}

.material-search {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #f5f5f5;
}

.search-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
}

.search-header h3 {
  margin: 0;
}

.search-tabs {
  display: flex;
  gap: 10px;
  font-size: 0.9em;
}

.search-tabs span {
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  color: #666;
}

.search-tabs span.active {
  background-color: #E3F2FD;
  color: #1976D2;
  font-weight: bold;
}

.search-bar {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 15px;
}

.input-row {
  display: flex;
  gap: 10px;
}

.input-row input {
  flex: 1;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.item-details {
  display: flex;
  gap: 15px;
  margin-top: 5px;
  font-size: 0.85em;
  color: #666;
}

.sales-info {
  color: #2E7D32;
  font-weight: bold;
}

.search-bar button {
  padding: 8px 15px;
  background-color: #1976D2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.search-bar button:disabled {
  background-color: #ccc;
}

.search-results {
  flex: 1;
  overflow-y: auto;
  border: 1px solid #eee;
  background: #fff;
}

.result-item {
  padding: 10px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
}

.result-item:hover {
  background-color: #f5f5f5;
}

.result-item:not(.segment-item).active {
  background-color: #e3f2fd;
  border-left: 4px solid #1976d2;
  padding-left: 6px; /* Adjust padding to compensate for border */
}

.result-item.active-playing {
  background-color: #FFEBEE !important;
  border-left: 4px solid #E53935 !important;
  padding-left: 6px !important;
  box-shadow: 0 0 4px rgba(229, 57, 53, 0.2);
}

.date-divider {
  padding: 6px 12px;
  background-color: #f8f9fa;
  color: #666;
  font-size: 0.8em;
  font-weight: bold;
  border-bottom: 1px solid #edf0f2;
  display: flex;
  align-items: center;
  gap: 4px;
}

.result-item.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #f9f9f9;
}

.item-title {
  font-weight: bold;
  margin-bottom: 5px;
}

.item-info {
  font-size: 0.8em;
  color: #666;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  line-height: 1.6;
}

.tag-recorded {
  background-color: #E8F5E9;
  color: #2E7D32;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.9em;
}

.tag-unrecorded {
  background-color: #FFEBEE;
  color: #C62828;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.9em;
}

.tag-influencer {
  background-color: #E3F2FD;
  color: #1565C0;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.85em;
  display: flex;
  align-items: center;
  gap: 4px;
}

.tag-abstract {
  background-color: #F3E5F5;
  color: #7B1FA2;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.85em;
}

.tag-product {
  background-color: #FFF3E0;
  color: #E65100;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.85em;
  display: flex;
  align-items: center;
  gap: 4px;
}

.item-info i {
  font-size: 1.1em;
  margin-right: 2px;
}

.matches-container {
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: #f9f9f9;
  padding: 6px 8px;
  border-radius: 4px;
  width: 100%;
}

.match-item {
  font-size: 0.85em;
  color: #333;
  line-height: 1.4;
  word-break: break-all;
}

.match-item :deep(mark) {
  background-color: yellow;
  color: #d32f2f;
  font-weight: bold;
  padding: 0 2px;
  border-radius: 2px;
}

</style>
